import { User, LaserMachine, UserPermissions, UserRole } from '../types';

/**
 * Get permissions for a user role
 */
export const getUserPermissions = (role: UserRole): UserPermissions => {
    switch (role) {
        case 'administrator':
            return {
                canViewAllMachines: true,
                canManageAllMachines: true,
                canAccessAdminPanel: true,
                canManageUsers: true,
                canManageCompanies: true,
                canManageResellers: true,
                canManageCustomers: true,
                canAssignMachines: true,
                canViewOwnMachinesOnly: false,
            };

        case 'reseller':
            return {
                canViewAllMachines: false, // Only client machines
                canManageAllMachines: false,
                canAccessAdminPanel: false,
                canManageUsers: false,
                canManageCompanies: false,
                canManageResellers: false,
                canManageCustomers: true,
                canAssignMachines: true,
                canViewOwnMachinesOnly: false,
            };

        case 'client':
        case 'customer':
            return {
                canViewAllMachines: false, // Only own machines
                canManageResellers: false,
                canManageCustomers: false,
                canAssignMachines: false,
                canViewOwnMachinesOnly: true,
                canManageAllMachines: false,
                canAccessAdminPanel: false,
                canManageUsers: false,
                canManageCompanies: false,
            };

        default:
            return {
                canViewAllMachines: false,
                canManageResellers: false,
                canManageCustomers: false,
                canAssignMachines: false,
                canViewOwnMachinesOnly: true,
                canManageAllMachines: false,
                canAccessAdminPanel: false,
                canManageUsers: false,
                canManageCompanies: false,
            };
    }
};

/**
 * Check if user can access a specific machine
 */
export const canAccessMachine = (user: User, machine: LaserMachine): boolean => {
    // Administrator can access all machines
    if (user.role === 'administrator') {
        return true;
    }

    // Customer/Client can access machines of their company
    if (user.role === 'client' || user.role === 'customer') {
        return machine.companyId === user.companyId;
    }

    // Reseller can access machines of their clients
    if (user.role === 'reseller') {
        // Can access if they are the reseller of this machine
        if (machine.resellerId === user.id) {
            return true;
        }
        // Can access if the machine owner is one of their clients
        if (user.clientIds && machine.ownerId && user.clientIds.includes(machine.ownerId)) {
            return true;
        }
        return false;
    }

    return false;
};

/**
 * Check if user can manage (control/edit) a specific machine
 */
export const canManageMachine = (user: User, machine: LaserMachine): boolean => {
    // Administrator can manage all machines
    if (user.role === 'administrator') {
        return true;
    }

    // Customer/Client can manage machines of their company
    if (user.role === 'client' || user.role === 'customer') {
        return machine.companyId === user.companyId;
    }

    // Reseller can view but typically not directly manage client machines
    // (they can monitor but clients control their own machines)
    if (user.role === 'reseller') {
        return false;
    }

    return false;
};

/**
 * Filter machines based on user role and permissions
 */
export const filterMachinesByRole = (machines: LaserMachine[], user: User): LaserMachine[] => {
    // Administrator sees all machines
    if (user.role === 'administrator') {
        return machines;
    }

    // Customer/Client sees only machines of their company
    if (user.role === 'client' || user.role === 'customer') {
        return machines.filter(machine => machine.companyId === user.companyId);
    }

    // Reseller sees machines of their clients
    if (user.role === 'reseller') {
        return machines.filter(machine => {
            // Machine sold by this reseller
            if (machine.resellerId === user.id) {
                return true;
            }
            // Machine owned by one of reseller's clients
            if (user.clientIds && machine.ownerId && user.clientIds.includes(machine.ownerId)) {
                return true;
            }
            return false;
        });
    }

    return [];
};

/**
 * Check if user can access admin panel
 */
export const canAccessAdminPanel = (user: User): boolean => {
    return user.role === 'administrator';
};

/**
 * Get role display name in Italian
 */
export const getRoleDisplayName = (role: UserRole): string => {
    switch (role) {
        case 'administrator':
            return 'Amministratore';
        case 'reseller':
            return 'Rivenditore';
        case 'client':
        case 'customer':
            return 'Cliente';
        default:
            return 'Utente';
    }
};

/**
 * Get role color for badges
 */
export const getRoleColor = (role: UserRole): string => {
    switch (role) {
        case 'administrator':
            return '#e91e63'; // Pink/Magenta
        case 'reseller':
            return '#ff9800'; // Orange
        case 'client':
        case 'customer':
            return '#2196f3'; // Blue
        default:
            return '#9e9e9e'; // Gray
    }
};
